/**
 * useScannerSSE
 *
 * Manages the lifecycle of a scanner Server-Sent Events connection.
 * Scoped to a single workflow/page — not a global stream.
 *
 * Responsibilities:
 * - Open / close EventSource for the scanner events URL
 * - Parse `scan-result` and `scan-error` SSE events into typed payloads
 * - Invoke caller-supplied callbacks for each event type
 * - Expose reactive `isConnected` and `connectionError` state
 * - Auto-disconnect on component unmount
 *
 * Usage:
 *   const sse = useScannerSSE({
 *     onScanResult: (event) => { ... },
 *     onScanError:  (event) => { ... },
 *   });
 *   sse.connect();
 *   // later:
 *   sse.disconnect();
 */
import { ref, onUnmounted } from 'vue';
import { buildScannerEventsUrl } from '@/shared/services/device-config.api';
 
// ── Types ──────────────────────────────────────────────────────────────────
 
export type ScannerSseResultEvent = {
  /** Backend-generated correlation id for the scan job */
  correlationId: string | null;
  /** Client-generated id supplied on the scan request */
  clientCorrelationId: string | null;
  /** Scan session grouping id */
  scanSessionId: string | null;
  /** Backend status string (e.g. 'Decoded', 'NoBarcode', 'ScannerBusy') */
  status: string;
  /** Whether the scan decoded a barcode successfully */
  success: boolean;
  /** Scan job payload — contains the decoded barcode data */
  scanJob?: unknown;
  /** Human-readable error description (null on success) */
  errorMessage?: string | null;
  /** Scan mode used by the backend ('image' | 'command' | 'auto') */
  scanMode?: string;
  /** True when the backend fell back from command mode to image mode */
  fallbackUsed?: boolean;
};
 
// ── Composable ─────────────────────────────────────────────────────────────
 
export function useScannerSSE(params: {
  onScanResult: (event: ScannerSseResultEvent) => void;
  onScanError: (event: ScannerSseResultEvent) => void;
}) {
  const isConnected = ref(false);
  const connectionError = ref<string | null>(null);
  let source: EventSource | null = null;
 
  function connect() {
    disconnect();
 
    const url = buildScannerEventsUrl();
    console.debug(`[SCANNER-SSE] Opening EventSource — url=${url}`);
    source = new EventSource(url, { withCredentials: true });
 
    source.onopen = () => {
      isConnected.value = true;
      connectionError.value = null;
      console.debug('[SCANNER-SSE] Stream opened');
    };
 
    source.addEventListener('scan-result', (evt: MessageEvent) => {
      try {
        const payload = JSON.parse(evt.data) as ScannerSseResultEvent;
        console.debug('[SCANNER-SSE] scan-result received', {
          correlationId: payload.correlationId,
          clientCorrelationId: payload.clientCorrelationId,
          status: payload.status,
          success: payload.success,
          scanMode: payload.scanMode,
          fallbackUsed: payload.fallbackUsed,
        });
        params.onScanResult(payload);
      } catch {
        console.warn('[SCANNER-SSE] scan-result parse failed — malformed payload', evt.data);
      }
    });
 
    source.addEventListener('scan-error', (evt: MessageEvent) => {
      try {
        const payload = JSON.parse(evt.data) as ScannerSseResultEvent;
        console.warn('[SCANNER-SSE] scan-error received', {
          correlationId: payload.correlationId,
          clientCorrelationId: payload.clientCorrelationId,
          status: payload.status,
          errorMessage: payload.errorMessage,
        });
        params.onScanError(payload);
      } catch {
        console.warn('[SCANNER-SSE] scan-error parse failed — malformed payload', evt.data);
      }
    });
 
    // scanner-heartbeat: no-op (keeps connection alive, no UI action needed)
    source.addEventListener('scanner-heartbeat', () => {
      isConnected.value = true;
      console.debug('[SCANNER-SSE] heartbeat received');
    });
 
    source.onerror = () => {
      isConnected.value = false;
      connectionError.value = 'Scanner SSE disconnected';
      console.warn('[SCANNER-SSE] Stream error / disconnected — EventSource will retry automatically');
    };
  }
 
  function disconnect() {
    if (source) {
      source.close();
      source = null;
      console.debug('[SCANNER-SSE] Stream closed (disconnect called)');
    }
    isConnected.value = false;
  }
 
  function reconnect() {
    connect();
  }
 
  onUnmounted(disconnect);
 
  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    reconnect,
  };
}